import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoginMutation } from '@/redux/slices/auth/authApiSlice';
import { toast } from 'sonner';
import { BadgeCheck, AlertTriangle } from 'lucide-react';
import oktaService from '@/services/OktaService';
import Loading from '@/components/Loading';
import { disableMockMode } from '@/services/mockAuth';

const OktaCallback = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [login] = useLoginMutation();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [authSuccess, setAuthSuccess] = useState(false);

    useEffect(() => {
        const handleCallback = async () => {
            const code = searchParams.get('code');

            if (!code) {
                // Don't show error immediately, just navigate back to login
                setTimeout(() => {
                    navigate('/login');
                }, 500);
                return;
            }

            try {
                disableMockMode();
                // Process the callback from Okta
                const user = await oktaService.handleCallback();

                // Send the token to our backend
                await login({
                    oktaToken: user.access_token
                }).unwrap();

                // Set success state before showing toast
                setAuthSuccess(true);
                setIsLoading(false);

                toast(
                    <div className="bg-states-success flex-center justify-start gap-2 w-[300px] p-3 rounded-md text-white">
                        <BadgeCheck />
                        <p className="semi-bold-16">Logged In Successfully</p>
                    </div>
                );

                // Short delay before redirecting to home to allow toast to be seen
                setTimeout(() => {
                    navigate('/');
                }, 1000);
            } catch (err) {
                console.error('Callback handling error:', err);
                setError('Failed to process login. Please try again.');
                setIsLoading(false);

                // Redirect back to login page after error
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        };

        // Small delay to prevent flash of error screen
        const timeoutId = setTimeout(() => {
            handleCallback();
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [login, navigate, searchParams]);

    // Initially just show loading state
    if (isLoading) {
        return <Loading />;
    }

    // Only show success screen after successful authentication
    if (authSuccess) {
        return (
            <div className="flex-center h-screen">
                <div className="text-center">
                    <h2 className="text-xl font-semibold">Authentication Successful</h2>
                    <p>Redirecting to application...</p>
                </div>
            </div>
        );
    }

    // Show error state only if there's a specific error
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-screen p-4">
                <div className="text-center mb-8">
                    <div className="bg-states-error flex items-center justify-start gap-2 p-3 rounded-md text-white mb-4">
                        <AlertTriangle />
                        <p className="semi-bold-16">Login Failed</p>
                    </div>
                    <p className="text-red-600 mb-4">{error}</p>
                </div>
            </div>
        );
    }

    // Fallback - should rarely be seen
    return <Loading />;
};

export default OktaCallback; 
